import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Col,
  FormControl,
  FormGroup,
  Row,
} from 'react-bootstrap';
import { observer } from 'mobx-react';
import DateTimePicker from 'react-datetime';
import './Ticket.css';
import Filters from '../../components/Filters/Filters';
import ReactTable from 'react-table';
import PaginationTable from '../../components/Pagination/PaginationTable';
import getSortHeaderProps from '../../utils/get-sorted-headers';
import { Loader } from '../../components/PageLoading/PageLoading';
import { withTranslation } from 'react-i18next';
import { useStores } from '../../hooks/use-stores';
import {
  displayDate,
  getLocaleName,
} from '../../utils/date';

const SimpleTicket = observer((props) => {
  const { t } = props;
  const { ticketStore, newAuthStore } = useStores();
  const [ loading, setLoading ] = useState(false);
  const [ filter, setFilter ] = useState('');
  const [ sorted, setSorted ] = useState([]);
  const [ selected, setSelected ] = useState([]);
  const { simpleTickets, simpleTicketsDateFrom, simpleTicketsDateTo } = ticketStore;
  const { user } = newAuthStore;
  const isMounted = useRef(true);

  useEffect(() => {
    ticketStore.loadSimpleTickets();

    return () => {
      isMounted.current = false;
    };
  }, [ticketStore]);

  const onTicketClick = (ticket) => {
    return props.onTicketClick(ticket);
  };

  const search = () => {
    setLoading(true);
    ticketStore.loadSimpleTickets()
      .then(() => {
        if (isMounted.current) {
          setLoading(false);
        }
      });
  };

  const onChangeDate = fieldName => value => {
    ticketStore[fieldName] = value;
    search();
  };

  const validFrom = (date) => date.isSameOrBefore(ticketStore.simpleTicketsDateTo);
  const validTo = (date) => date.isSameOrAfter(ticketStore.simpleTicketsDateFrom);

  const handleChange = (e) => {
    const filter = e.target.value;
    setFilter(filter);
  };

  const getColumns = () => {
    const { timezone } = ticketStore;

    return [
      {
        headerText: t('ID'),
        accessor: ticket => ticket,
        Cell: ticket => (
          <span title={ticket.original.isDemo ? 'Demo' : ''}
            className={ticket.original.isDemo ? 'ticket-demo' : ''}>
            {ticket.original.id}
          </span>
        ),
        id: 'id',
        width: 70,
        className: 'sticky',
        headerClassName: 'sticky',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Date'),
        accessor: ticket => ticket,
        Cell: ticket => (displayDate(ticket.original.date, timezone, 'LT L', user.language)),
        id: 'date',
        width: 150,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Material'),
        accessor: 'itemCode',
        id: 'itemCode',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Quantity'),
        accessor: ticket => ticket,
        Cell: ticket => (`${ticket.original.quantity || 0} ${ticket.original.unitSymbol || ''}`),
        id: 'quantity',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Device'),
        accessor: 'deviceDescription',
        id: 'deviceDescription',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Regular Ticket ID'),
        accessor: 'batchTicketId',
        id: 'batchTicketId',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('User'),
        accessor: 'userLogin',
        id: 'userLogin',
        ...getSortHeaderProps(sorted),
      },
      {
        accessor: 'actions',
        maxWidth: 50,
        Cell: row => (
          <div>
            <a className="btn btn-sm btn-default btn-action"
              onClick={() => onTicketClick(row.original)}>
              <i className="fa fa-file-text-o"/>
            </a>
          </div>
        ),
      },
    ];
  };

  return (
    <Row className="ticket-mngt">
      <Col sm={12}>
        <Row>
          <Col sm={4}>
            <FormGroup>
              <FormControl
                type="text"
                value={filter}
                placeholder={t('Search')}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>
          <Col sm={8}>
            <div className="input-group input-large date-picker input-daterange pull-right">
              <DateTimePicker
                inputProps={{ className: 'form-control', placeholder: 'date from' }}
                onChange={onChangeDate('simpleTicketsDateFrom')}
                value={simpleTicketsDateFrom}
                timeFormat={true}
                closeOnSelect={true}
                isValidDate={validFrom}
                locale={getLocaleName(user.language)}
              />
              <span className="input-group-addon"> - </span>
              <DateTimePicker
                inputProps={{ className: 'form-control', placeholder: 'date to' }}
                onChange={onChangeDate('simpleTicketsDateTo')}
                value={simpleTicketsDateTo}
                timeFormat={true}
                closeOnSelect={true}
                isValidDate={validTo}
                locale={getLocaleName(user.language)}
              />
            </div>
          </Col>
        </Row>
        {loading ? <Loader/> : (
          <Row>
            <Col sm={12}>
              <Filters filter={filter} data={simpleTickets} renderData={(tickets) => (
                <ReactTable
                  className="-highlight max-height-table"
                  data={tickets}
                  noDataText={' '}
                  columns={getColumns()}
                  sorted={sorted}
                  onSortedChange={sorted => setSorted(sorted)}
                  showPaginationTop={false}
                  showPaginationBottom={true}
                  showPageSizeOptions={true}
                  PaginationComponent={PaginationTable}
                  resizable={false}
                  minRows={1}
                  defaultPageSize={100}
                  getTrProps={(state, rowInfo) => {
                    if (rowInfo && rowInfo.row) {
                      return {
                        onClick: () => {
                          onTicketClick(rowInfo.original);
                          setSelected(rowInfo.index);
                        },
                        className: rowInfo.index === selected ? 'row-selected' : '',
                      };
                    }
                    return {};
                  }}
                />
              )}/>
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
});

export default withTranslation()(SimpleTicket);
